import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import Turnstile from '../Turnstile/Turnstile';
import { FiArrowLeft } from "react-icons/fi";
import api from '../../api/config';
import './css/MerkezForm.css';

const NevaYemekForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [photoError, setPhotoError] = useState('');
    const [ilanDetay, setIlanDetay] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error] = useState(null);
    const [kvkkConsent, setKvkkConsent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState('');
    const turnstileRef = useRef(null);

    const [user, setUser] = useState({
        ad: '', soyad: '', cinsiyet: '', mezuniyet: '', medeni_durum: '',
        surucu_belgesi: '', dogum_tarihi: '', email: '', ev_adresi: '',
        askerlik_durumu: '', job_id: '', cep_telefonu: '', ikinci_cep_telefonu: '',
        egitim: [{ okul_adi: '', bolum: '', baslangic_tarihi: '', bitis_tarihi: '' }],
        sertifika: [{ sertifika_adi: '', alindi_tarihi: '' }],
        is_deneyimi: [{ firma_adi: '', pozisyon: '', baslangic_tarihi: '', bitis_tarihi: '' }],
        yabanci_dil: [{ dil_adi: '', seviye: '' }],
        referans: [{ referans_adi: '', referans_iletisim: '' }],
        ilgi_alanlari: [{ ilgi_alani: '' }],
        cv: null, photo: null
    });

    const [photoPreview, setPhotoPreview] = useState(null);

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            setPhotoError('Dosya boyutu 5MB\'dan küçük olmalıdır');
            return;
        }
        if (!file.type.startsWith('image/')) {
            setPhotoError('Lütfen geçerli bir resim dosyası seçin');
            return;
        }
        setPhotoError('');
        const reader = new FileReader();
        reader.onload = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(file);
        setUser({ ...user, photo: file });
    };

    useEffect(() => {
        const fetchIlanDetay = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await api.get(`/api/neva-yemek-ilanlar/${id}`);
                setIlanDetay(response.data);
            } catch (err) {
                console.error('API hatası:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchIlanDetay();
    }, [id]);

    const handleFileChange = (e) => setUser({ ...user, cv: e.target.files[0] });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser(prev => ({ ...prev, [name]: value }));
    };

    const handleArrayChange = (e, index, field) => {
        const { name, value } = e.target;
        const newArray = [...user[field]];
        newArray[index][name] = value;
        setUser({ ...user, [field]: newArray });
    };

    const addToArray = (field) => {
        const entries = {
            egitim: { okul_adi: '', bolum: '', baslangic_tarihi: '', bitis_tarihi: '' },
            is_deneyimi: { firma_adi: '', pozisyon: '', baslangic_tarihi: '', bitis_tarihi: '' },
            sertifika: { sertifika_adi: '', alindi_tarihi: '' },
            yabanci_dil: { dil_adi: '', seviye: '' },
            referans: { referans_adi: '', referans_iletisim: '' },
            ilgi_alanlari: { ilgi_alani: '' }
        };
        setUser({ ...user, [field]: [...user[field], entries[field]] });
    };

    const removeFromArray = (field, index) => {
        setUser({ ...user, [field]: user[field].filter((_, i) => i !== index) });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        if (!user.photo) { setPhotoError('Lütfen bir fotoğraf seçin'); return; }
        if (!kvkkConsent) { alert('Lütfen KVKK metnini onaylayınız.'); return; }
        if (!turnstileToken) { alert('Lütfen güvenlik doğrulamasını tamamlayınız.'); return; }

        setIsSubmitting(true);

        try {
            const userData = {
                ad: user.ad, soyad: user.soyad, cinsiyet: user.cinsiyet,
                mezuniyet: user.mezuniyet, email: user.email,
                medeni_durum: user.medeni_durum, surucu_belgesi: user.surucu_belgesi,
                askerlik_durumu: user.askerlik_durumu, dogum_tarihi: user.dogum_tarihi,
                ev_adresi: user.ev_adresi, cep_telefonu: user.cep_telefonu,
                ikinci_cep_telefonu: user.ikinci_cep_telefonu, job_id: user.job_id,
                turnstileToken
            };

            const userResponse = await api.post('/api/users', userData);
            const userId = userResponse.data.user_id;

            if (user.photo) {
                const photoFormData = new FormData();
                photoFormData.append('photo', user.photo);
                photoFormData.append('userId', userId);
                await api.post('/api/photo/upload', photoFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            if (user.cv) {
                const formData = new FormData();
                formData.append('cv', user.cv);
                formData.append('userId', userId);
                await api.post('/api/cv/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            const basvuruResponse = await api.post('/api/basvurular', {
                userId, ilanId: id, ilantype: 'neva_yemek'
            });

            if (!basvuruResponse.data.id) throw new Error('Başvuru kaydedilemedi');

            await Promise.all([
                api.post('/api/users/egitim', { egitimBilgileri: user.egitim, userId }),
                api.post('/api/users/is_deneyimi', { isDeneyimiBilgileri: user.is_deneyimi, userId }),
                api.post('/api/users/sertifika', { sertifikaBilgileri: user.sertifika, userId }),
                api.post('/api/users/yabanci_dil', { yabanciDilBilgileri: user.yabanci_dil, userId }),
                api.post('/api/users/referanslar', { referansBilgileri: user.referans, userId }),
                api.post('/api/users/ilgi_alanlari', { ilgiAlanlari: user.ilgi_alanlari, userId })
            ]);

            navigate('/success');
        } catch (error) {
            console.error('Hata:', error);
            alert('Form gönderilirken bir hata oluştu: ' + error.message);
            turnstileRef.current?.reset();
            setTurnstileToken('');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <Header />
            <div className='MerkezFormRadio'>
                <div className='ilan-detayları'>
                    <div className='ilanDetaylarıRadio'>
                        {loading && ""}
                        {error && <p style={{ color: 'red' }}>{error}</p>}
                        {ilanDetay && (
                            <div className="ilan-detay">
                                <div className='ilanDetayPosition'>
                                    <div className='backİcon'>
                                        <Link to="/"><FiArrowLeft /></Link>
                                        <p className='ilanTitle'>Pozisyon Başlığı: <span>{ilanDetay.ilan_basligi}</span></p>
                                    </div>
                                    <div className='ilan-descs-banner'>
                                        <p className='ilanŞirketAdı'>Kurum Adı: <br /><span>{ilanDetay.firma_adi}</span></p>
                                        <p className='İlanÇalışmaTürü'>Çalışma Türü: <br /><span>{ilanDetay.is_tipi}</span></p>
                                        <p className='İlanTarih'>Son Başvuru: <br /><span>{new Date(ilanDetay.ilan_tarihi).toLocaleDateString()}</span></p>
                                        <p className='İlanNo'>İlan Numarası: <br /><span>{ilanDetay.job_id}</span></p>
                                    </div>
                                </div>
                                <div className='ilanDetayDesc'>
                                    <p>POZİSYON DETAYLARI:</p>
                                    <div
                                        className='İlanDetay'
                                        dangerouslySetInnerHTML={{ __html: ilanDetay.detaylar }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <img className='ilan-desc-logo' src="https://www.onder.org.tr/build/assets/search-bg-842c8fc7.svg" alt="Onder Logo" />
                </div>

                <form onSubmit={handleSubmit}>
                    <div className='formUsersSetion'>
                        <div className='formPhoto'>
                            <div className="photo-upload-container">
                                <div className="photo-upload-preview">
                                    {photoPreview
                                        ? <img src={photoPreview} alt="Önizleme" className="photo-preview" />
                                        : <div className="photo-placeholder">Fotoğraf Yükleyin*</div>
                                    }
                                </div>
                                <label htmlFor="photo-upload" className="photo-upload-label">
                                    Fotoğraf Seç
                                    <input id="photo-upload" type="file" accept="image/*" onChange={handlePhotoChange} className="photo-input" />
                                </label>
                                {photoError && <div className="text-red-500 mt-2 text-sm">{photoError}</div>}
                            </div>
                        </div>

                        <div className='formUserOneSection'>
                            <div className='formNameSurname'>
                                <label>Adınızı Giriniz:</label>
                                <input name="ad" placeholder="Ad" value={user.ad} onChange={handleChange} required />
                            </div>
                            <div className='formNameSurname'>
                                <label>Soyadınızı Giriniz:</label>
                                <input name="soyad" placeholder="Soyad" value={user.soyad} onChange={handleChange} required />
                            </div>
                        </div>

                        <div className='formNameSurnameMail'>
                            <label>Mail Adresinizi Giriniz:</label>
                            <input type="email" name="email" placeholder="E-posta Adresi" value={user.email} onChange={handleChange} required />
                        </div>

                        <div className='formCinsiyetDogum'>
                            <div className='FCD'>
                                <label>Cinsiyetiniz Nedir:</label>
                                <select name="cinsiyet" value={user.cinsiyet} onChange={handleChange} required>
                                    <option value="">Cinsiyet Seçiniz</option>
                                    <option value="Erkek">Erkek</option>
                                    <option value="Kadın">Kadın</option>
                                </select>
                            </div>
                            <div className='FCD'>
                                <label>Doğum Tarihi:</label>
                                <input name="dogum_tarihi" type="date" value={user.dogum_tarihi} onChange={handleChange} required />
                            </div>
                        </div>

                        <div className='FormAdress'>
                            <label>Ev Adresi Giriniz:</label>
                            <input className='formAdress' name="ev_adresi" placeholder="Ev Adresi" value={user.ev_adresi} onChange={handleChange} required />
                        </div>

                        <div className='formTel'>
                            <div className='FTForm'>
                                <label>Cep Telefon Numaranızı Giriniz:</label>
                                <input name="cep_telefonu" placeholder="Cep Telefonu" value={user.cep_telefonu} onChange={handleChange} required />
                            </div>
                            <div className='FTForm'>
                                <label>Yedek Numara Giriniz:</label>
                                <input name="ikinci_cep_telefonu" placeholder="İkinci Cep Telefonu" value={user.ikinci_cep_telefonu} onChange={handleChange} />
                            </div>
                        </div>

                        <div className='MMASForm'>
                            <div className='MMForm'>
                                <label>Mezuniyet Durumu:</label>
                                <select name="mezuniyet" value={user.mezuniyet} onChange={handleChange} required>
                                    <option value="">Mezuniyet Durumunuz</option>
                                    <option value="İlkokul">İlkokul</option>
                                    <option value="Ortaokul">Ortaokul</option>
                                    <option value="Lise">Lise</option>
                                    <option value="üniversite">Üniversite</option>
                                </select>
                            </div>
                            <div className='MMForm'>
                                <label>Medeni Hâl:</label>
                                <select name="medeni_durum" value={user.medeni_durum} onChange={handleChange} required>
                                    <option value="">Medeni Durumu Seçiniz</option>
                                    <option value="Evli">Evli</option>
                                    <option value="Bekâr">Bekâr</option>
                                </select>
                            </div>
                        </div>

                        <div className='ASForm'>
                            <div className='ASFormTekil'>
                                <label>Askerlik Durumu:</label>
                                <select name="askerlik_durumu" value={user.askerlik_durumu} onChange={handleChange} required>
                                    <option value="">Askerlik Durumu Seçiniz</option>
                                    <option value="Yapıldı">Yapıldı</option>
                                    <option value="Muaf">Muaf</option>
                                    <option value="Tecilli">Tecilli</option>
                                    <option value="Yapılmadı">Yapılmadı</option>
                                </select>
                            </div>
                            <div className='ASFormTekil'>
                                <label>Sürücü Belgesi:</label>
                                <select name="surucu_belgesi" value={user.surucu_belgesi} onChange={handleChange} required>
                                    <option value="">Sürücü Belgesi</option>
                                    <option value="A Sınıfı">A Sınıfı</option>
                                    <option value="B Sınıfı">B Sınıfı</option>
                                    <option value="C Sınıfı">C Sınıfı</option>
                                    <option value="D Sınıfı">D Sınıfı</option>
                                </select>
                            </div>
                        </div>

                        <h3 className='TTitle'>Eğitim Bilgileri</h3>
                        {user.egitim.map((item, index) => (
                            <div className="merkez-desc-bot-ilan" key={index}>
                                <input name="okul_adi" placeholder="Okul Adı" value={item.okul_adi} onChange={(e) => handleArrayChange(e, index, 'egitim')} />
                                <input name="bolum" placeholder="Bölüm" value={item.bolum} onChange={(e) => handleArrayChange(e, index, 'egitim')} />
                                <div className='input-data'>
                                    <input name="baslangic_tarihi" type="date" value={item.baslangic_tarihi} onChange={(e) => handleArrayChange(e, index, 'egitim')} />
                                    <input name="bitis_tarihi" type="date" value={item.bitis_tarihi} onChange={(e) => handleArrayChange(e, index, 'egitim')} />
                                </div>
                                {user.egitim.length > 1 && (
                                    <button type="button" className="entry-remove-btn" onClick={() => removeFromArray('egitim', index)} aria-label="Kaldır">×</button>
                                )}
                            </div>
                        ))}
                        <button className='TButton' type="button" onClick={() => addToArray('egitim')}>Eğitim Bilgisi Ekle</button>

                        <h3 className='TTitle'>İş Deneyimi Bilgileri</h3>
                        {user.is_deneyimi.map((item, index) => (
                            <div className="merkez-desc-bot-ilan" key={index}>
                                <input name="firma_adi" placeholder="Firma Adı" value={item.firma_adi} onChange={(e) => handleArrayChange(e, index, 'is_deneyimi')} />
                                <input name="pozisyon" placeholder="Pozisyon" value={item.pozisyon} onChange={(e) => handleArrayChange(e, index, 'is_deneyimi')} />
                                <div className='input-data'>
                                    <input name="baslangic_tarihi" type="date" value={item.baslangic_tarihi} onChange={(e) => handleArrayChange(e, index, 'is_deneyimi')} />
                                    <input name="bitis_tarihi" type="date" value={item.bitis_tarihi} onChange={(e) => handleArrayChange(e, index, 'is_deneyimi')} />
                                </div>
                                {user.is_deneyimi.length > 1 && (
                                    <button type="button" className="entry-remove-btn" onClick={() => removeFromArray('is_deneyimi', index)} aria-label="Kaldır">×</button>
                                )}
                            </div>
                        ))}
                        <button className='TButton' type="button" onClick={() => addToArray('is_deneyimi')}>İş Deneyimi Ekle</button>

                        <h3 className='TTitle'>Sertifika Bilgileri</h3>
                        {user.sertifika.map((item, index) => (
                            <div className="merkez-desc-bot-ilan" key={index}>
                                <input name="sertifika_adi" placeholder="Sertifika Adı" value={item.sertifika_adi} onChange={(e) => handleArrayChange(e, index, 'sertifika')} />
                                <input name="alindi_tarihi" type="date" value={item.alindi_tarihi} onChange={(e) => handleArrayChange(e, index, 'sertifika')} />
                                {user.sertifika.length > 1 && (
                                    <button type="button" className="entry-remove-btn" onClick={() => removeFromArray('sertifika', index)} aria-label="Kaldır">×</button>
                                )}
                            </div>
                        ))}
                        <button className='TButton' type="button" onClick={() => addToArray('sertifika')}>Sertifika Ekle</button>

                        <h3 className='TTitle'>Yabancı Dil Bilgileri</h3>
                        {user.yabanci_dil.map((item, index) => (
                            <div className="merkez-desc-bot-ilan" key={index}>
                                <input name="dil_adi" placeholder="Dil Adı" value={item.dil_adi} onChange={(e) => handleArrayChange(e, index, 'yabanci_dil')} />
                                <select name="seviye" value={item.seviye} onChange={(e) => handleArrayChange(e, index, 'yabanci_dil')} >
                                    <option value="">Seviye Seçiniz</option>
                                    <option value="Başlangıç (Elementary)">Başlangıç (Elementary)</option>
                                    <option value="Orta (Intermediate)">Orta (Intermediate)</option>
                                    <option value="İyi (Upper Intermediate)">İyi (Upper Intermediate)</option>
                                    <option value="Mükemmel (Advanced)">Mükemmel (Advanced)</option>
                                </select>
                                {user.yabanci_dil.length > 1 && (
                                    <button type="button" className="entry-remove-btn" onClick={() => removeFromArray('yabanci_dil', index)} aria-label="Kaldır">×</button>
                                )}
                            </div>
                        ))}
                        <button className='TButton' type="button" onClick={() => addToArray('yabanci_dil')}>Yabancı Dil Ekle</button>

                        <h3 className='TTitle'>Referans Bilgileri</h3>
                        {user.referans.map((item, index) => (
                            <div className="merkez-desc-bot-ilan" key={index}>
                                <input name="referans_adi" placeholder="Referans Adı" value={item.referans_adi} onChange={(e) => handleArrayChange(e, index, 'referans')} />
                                <input name="referans_iletisim" placeholder="Referans İletişim" value={item.referans_iletisim} onChange={(e) => handleArrayChange(e, index, 'referans')} />
                                {user.referans.length > 1 && (
                                    <button type="button" className="entry-remove-btn" onClick={() => removeFromArray('referans', index)} aria-label="Kaldır">×</button>
                                )}
                            </div>
                        ))}
                        <button className='TButton' type="button" onClick={() => addToArray('referans')}>Referans Ekle</button>

                        <h3 className='TTitle'>İlgi Alanları</h3>
                        {user.ilgi_alanlari.map((item, index) => (
                            <div className="merkez-desc-bot-ilan" key={index}>
                                <input name="ilgi_alani" placeholder="İlgi Alanı" value={item.ilgi_alani} onChange={(e) => handleArrayChange(e, index, 'ilgi_alanlari')} />
                                {user.ilgi_alanlari.length > 1 && (
                                    <button type="button" className="entry-remove-btn" onClick={() => removeFromArray('ilgi_alanlari', index)} aria-label="Kaldır">×</button>
                                )}
                            </div>
                        ))}
                        <button className='TButton' type="button" onClick={() => addToArray('ilgi_alanlari')}>İlgi Alanı Ekle</button>

                        <div className='formCVUpload'>
                            <label htmlFor="cv-upload">CV / Özgeçmiş Yükle</label>
                            <input className='file-input' type="file" id="cv-upload" name="cv" onChange={handleFileChange} accept=".pdf,.doc,.docx" />
                            {user.cv && <span className="file-selected">{user.cv.name}</span>}
                        </div>

                        <div className='kvkk-consent'>
                            <label className='kvkk-label'>
                                <input type="checkbox" checked={kvkkConsent} onChange={(e) => setKvkkConsent(e.target.checked)} required />
                                <span className='kvkk-text'>
                                    Kişisel verilerimin işlenmesine ilişkin aydınlatma metnini okudum ve anladım.
                                    Kişisel verilerimin belirtilen kapsamda işlenmesini onaylıyorum.
                                </span>
                            </label>
                        </div>

                        <Turnstile ref={turnstileRef} onVerify={setTurnstileToken} />
                    </div>
                    <button className='SubmitButton' type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Gönderiliyor...' : 'Başvuruyu Gönder'}
                    </button>
                </form>
            </div>
            <Footer />
        </div>
    );
};

export default NevaYemekForm;